// 2026-07-29 (user directive — "כל השעונים וכל הזמנים צריכים להיות מתוקנים לפי שעון
// ישראל"): the server (OS + MySQL SYSTEM time_zone + the Node/pm2 process) all run in
// America/New_York — confirmed via `timedatectl` + `SELECT @@session.time_zone, NOW()`.
// Every DATETIME column is written as a NAIVE string with no timezone tag (MySQL
// doesn't store one), e.g. "2026-07-29 18:16:08" — that's Eastern wall-clock time.
//
// The bug: `new Date(dbString).toLocaleString(...)` — anywhere it runs — parses that
// naive string as if it were ALREADY in the LOCAL zone of whatever machine does the
// parsing. In a 'use client' component that's the admin's own browser; if the admin's
// OS is set to Israel time, "18:16:08" gets treated as 18:16 Israel time even though it
// really means 18:16 Eastern (= 01:16 the NEXT day in Israel, a 7h error in July). This
// has nothing to do with the *display* timezone (`timeZone: 'Asia/Jerusalem'` alone
// does not fix it) — the SOURCE string must first be correctly anchored to the zone it
// was actually written in, THEN converted for display.
//
// Fix here is intentionally NOT a DB/server timezone migration (too much blast radius —
// every other cron/pipeline on this server assumes America/New_York) — it's a pure,
// safe display-layer correction: parseServerDatetime() anchors a naive DB string to
// SERVER_TZ (America/New_York) to get the true absolute instant; fmtIL*() then always
// renders that instant in Asia/Jerusalem, regardless of who (server process or a
// visitor's browser in any timezone) evaluates the formatting call.
//
// Use these for ANY value that came from a MySQL DATETIME/TIMESTAMP column. Do NOT use
// parseServerDatetime() on a value that already carries explicit zone info (an ISO
// string with 'Z' or a '+HH:MM' offset, or a client-side `Date.now()`/`new Date()`) —
// those already represent a real absolute instant and only need the fmtIL* display step.

const SERVER_TZ = 'America/New_York';
const ISRAEL_TZ = 'Asia/Jerusalem';

/** True if a string already carries explicit UTC/offset info (safe to `new Date()` directly). */
function hasExplicitZone(s: string): boolean {
  return /Z$|[+-]\d{2}:?\d{2}$/.test(s.trim());
}

/**
 * Anchors a naive "YYYY-MM-DD HH:MM:SS" (or ISO-without-zone) string — as actually
 * written by this server's MySQL (SYSTEM tz = America/New_York) — to that zone, and
 * returns the real Date (absolute instant). Safe/no-op on already-zoned strings.
 */
export function parseServerDatetime(raw: string | Date | null | undefined): Date | null {
  if (!raw) return null;
  // A real Date instance (e.g. client-side `new Date()`) already represents a correct
  // absolute instant — no server-zone anchoring needed, just pass it through so the
  // fmtIL* functions below can still apply the Asia/Jerusalem display step.
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
  const s = String(raw).trim();
  if (!s) return null;
  if (hasExplicitZone(s)) return new Date(s);

  const m = s.replace('T', ' ').match(/^(\d{4})-(\d{2})-(\d{2})[ ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  const year = Number(m[1]), month = Number(m[2]), day = Number(m[3]);
  const hour = Number(m[4]), minute = Number(m[5]), second = Number(m[6] || '0');

  // Double-format trick to find SERVER_TZ's UTC offset for this exact wall-clock
  // moment (handles DST correctly — EDT vs EST differ by an hour, and Israel's own DST
  // calendar doesn't line up with the US one, so a fixed offset would be wrong part of
  // the year).
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: SERVER_TZ, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts: Record<string, string> = {};
  for (const p of dtf.formatToParts(new Date(utcGuess))) if (p.type !== 'literal') parts[p.type] = p.value;
  const asIfUtc = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour) === 24 ? 0 : Number(parts.hour), Number(parts.minute), Number(parts.second)
  );
  const offsetMs = asIfUtc - utcGuess;
  return new Date(utcGuess - offsetMs);
}

/** "29/07 18:16" style — day/month + hour:minute, always in Israel time. */
export function fmtILDateTime(raw: string | Date | null | undefined): string {
  // Founder 2026-09-27: "27/09/26, 18:16" — the house date format with the time
  const d = parseServerDatetime(raw);
  if (!d) return '—';
  return `${fmtILDate(d)}, ${d.toLocaleTimeString('he-IL', { timeZone: ISRAEL_TZ, hour: '2-digit', minute: '2-digit' })}`;
}

/** "18:16" — time only (HH:MM), always in Israel time. Founder 2026-09-27: tables show minutes; fmtILTimeSec keeps seconds for timelines. */
export function fmtILTime(raw: string | Date | null | undefined): string {
  const d = parseServerDatetime(raw);
  if (!d) return '—';
  return d.toLocaleTimeString('he-IL', { timeZone: ISRAEL_TZ, hour: '2-digit', minute: '2-digit' });
}
export function fmtILTimeSec(raw: string | Date | null | undefined): string {
  const d = parseServerDatetime(raw);
  if (!d) return '—';
  return d.toLocaleTimeString('he-IL', { timeZone: ISRAEL_TZ, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/** "29 ביולי 2026" style — full date, always in Israel time. */
export function fmtILDate(raw: string | Date | null | undefined): string {
  // Founder 2026-09-27: house date format = 27/09/26 (dd/MM/yy), everywhere
  const d = parseServerDatetime(raw);
  if (!d) return '—';
  const p = new Intl.DateTimeFormat('en-GB', { timeZone: ISRAEL_TZ, day: '2-digit', month: '2-digit', year: '2-digit' }).formatToParts(d);
  const g = (t: string) => p.find(x => x.type === t)?.value || '';
  return `${g('day')}/${g('month')}/${g('year')}`;
}
const WEEKDAYS_HE = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
/** "רביעי" — the Hebrew weekday of the calendar day in Israel (Founder 2026-09-27: a יום column beside every date). */
export function fmtILWeekday(raw: string | Date | null | undefined): string {
  const d = parseServerDatetime(raw);
  if (!d) return '—';
  const wd = new Intl.DateTimeFormat('en-US', { timeZone: ISRAEL_TZ, weekday: 'short' }).format(d);
  return WEEKDAYS_HE[['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(wd)] || '—';
}

/** Full custom formatting, always pinned to Israel time — pass any Intl.DateTimeFormatOptions. */
export function fmtIL(raw: string | Date | null | undefined, opts: Intl.DateTimeFormatOptions): string {
  const d = parseServerDatetime(raw);
  if (!d) return '—';
  return d.toLocaleString('he-IL', { ...opts, timeZone: ISRAEL_TZ });
}

/** "30.7.2026" — bare `.toLocaleDateString('he-IL')` equivalent, pinned to Israel time. */
export function fmtILDateDefault(raw: string | Date | null | undefined): string {
  return fmtILDate(raw); // Founder 2026-09-27: one house format (27/09/26)
}

/** "30.7.2026, 1:20:27" — bare `.toLocaleString('he-IL')` equivalent, pinned to Israel time. */
export function fmtILDefault(raw: string | Date | null | undefined): string {
  const d = parseServerDatetime(raw);
  if (!d) return '—';
  return `${fmtILDate(d)}, ${d.toLocaleTimeString('he-IL', { timeZone: ISRAEL_TZ })}`; // 27/09/26, 18:16:08
}

/** "30.07" — day.month only, always in Israel time (short list/table contexts). */
export function fmtILShortDate(raw: string | Date | null | undefined): string {
  const d = parseServerDatetime(raw);
  if (!d) return '—';
  return d.toLocaleDateString('he-IL', { timeZone: ISRAEL_TZ, day: '2-digit', month: '2-digit' });
}

/** "2026-07-30" — sortable ISO date, but the CALENDAR DAY as it is in Israel right now
 *  (not `new Date().toISOString().slice(0,10)`'s UTC day, which can be a day off around
 *  Israel's own midnight). For filenames/exports timestamped "today". */
export function isoDateIL(raw: string | Date | null | undefined = new Date()): string {
  const d = parseServerDatetime(raw);
  if (!d) return '';
  const parts: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('en-CA', { timeZone: ISRAEL_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d)) {
    if (p.type !== 'literal') parts[p.type] = p.value;
  }
  return `${parts.year}-${parts.month}-${parts.day}`;
}
